import React from 'react'
import cn from 'classnames'
import FormattedCurrency from 'js/components/FormattedCurrency'
import { FormattedMessage } from 'react-intl'
import ChooseSubscriptionText from './ChooseSubscriptionPriceText'

interface DurationProps {
  duration: 1 | 3 | 6 | 12 | 24
  discount: boolean
  price?: React.ReactNode
  originalPrice: number
  currency: string
  priceValue: number
  discountOnlyFirstPurchase: boolean
}

function getDurationFrequency({
  duration,
  discount,
  price,
  originalPrice,
  currency,
  priceValue,
  discountOnlyFirstPurchase,
}: DurationProps) {
  if (duration === 1)
    return (
      <FormattedMessage
        id="upgrade.billed_monthly"
        defaultMessage="Save {amount} for the first month"
        values={{
          amount: (
            <FormattedCurrency
              value={originalPrice - priceValue}
              currency={currency}
            />
          ),
        }}
      />
    )
  if (duration === 3)
    return discount ? (
      discountOnlyFirstPurchase ? (
        <FormattedMessage
          id="chooseSubscription.billed_quarterly"
          defaultMessage="Save {amount} for the first quarter"
          values={{
            amount: (
              <FormattedCurrency
                value={originalPrice - priceValue}
                currency={currency}
              />
            ),
          }}
        />
      ) : (
        <FormattedMessage
          id="chooseSubscription.billed_quarterly_forever"
          defaultMessage="Save {amount} quarterly"
          values={{
            amount: (
              <FormattedCurrency
                value={originalPrice - priceValue}
                currency={currency}
              />
            ),
          }}
        />
      )
    ) : (
      <FormattedMessage
        id="upgrade.billed_quarterly"
        defaultMessage="Billed {price} quarterly"
        values={{ price }}
      />
    )
  if (duration === 6)
    return discount ? (
      discountOnlyFirstPurchase ? (
        <FormattedMessage
          id="chooseSubscription.billed_twiceAYear"
          defaultMessage="Save {amount} for the first six months"
          values={{
            amount: (
              <FormattedCurrency
                value={originalPrice - priceValue}
                currency={currency}
              />
            ),
          }}
        />
      ) : (
        <FormattedMessage
          id="chooseSubscription.billed_twiceAYear_forever"
          defaultMessage="Save {amount} twice annually"
          values={{
            amount: (
              <FormattedCurrency
                value={originalPrice - priceValue}
                currency={currency}
              />
            ),
          }}
        />
      )
    ) : (
      <FormattedMessage
        id="upgrade.billed_twiceAYear"
        defaultMessage="Billed {price} twice annually"
        values={{ price }}
      />
    )
  if (duration === 12)
    return discount ? (
      discountOnlyFirstPurchase ? (
        <FormattedMessage
          id="chooseSubscription.billed_yearly"
          defaultMessage="Save {amount} for the first year"
          values={{
            amount: (
              <FormattedCurrency
                value={originalPrice - priceValue}
                currency={currency}
              />
            ),
          }}
        />
      ) : (
        <FormattedMessage
          id="chooseSubscription.billed_yearly_forever"
          defaultMessage="Save {amount} annually"
          values={{
            amount: (
              <FormattedCurrency
                value={originalPrice - priceValue}
                currency={currency}
              />
            ),
          }}
        />
      )
    ) : (
      <FormattedMessage
        id="upgrade.billed_yearly"
        defaultMessage="Billed {price} annually"
        values={{ price }}
      />
    )
  if (duration === 24)
    return discount ? (
      discountOnlyFirstPurchase ? (
        <FormattedMessage
          id="chooseSubscription.billed_everyOtherYear"
          defaultMessage="Save {amount} for the first two years"
          values={{
            amount: (
              <FormattedCurrency
                value={originalPrice - priceValue}
                currency={currency}
              />
            ),
          }}
        />
      ) : (
        <FormattedMessage
          id="chooseSubscription.billed_everyOtherYear_forever"
          defaultMessage="Save {amount} every two years"
          values={{
            amount: (
              <FormattedCurrency
                value={originalPrice - priceValue}
                currency={currency}
              />
            ),
          }}
        />
      )
    ) : (
      <FormattedMessage
        id="upgrade.billed_everyOtherYear"
        defaultMessage="Billed {price} every other year"
        values={{ price }}
      />
    )
}

type Props = {
  price: number
  pricePerMonth: number
  originalPrice: number
  duration: 1 | 3 | 6 | 12 | 24
  selected: boolean
  onClick: () => void
  currency: string
  bestPlanInfo: any
  trialDuration: number
  discount: number
  discountOnlyFirstPurchase: boolean
  isFallbackPrice: boolean
}

function ChooseSubscriptionProduct({
  price,
  pricePerMonth,
  originalPrice,
  duration,
  selected,
  onClick,
  currency,
  bestPlanInfo,
  trialDuration,
  discount,
  discountOnlyFirstPurchase = true,
  isFallbackPrice,
}: Props) {
  const durationText = getDurationFrequency({
    duration,
    discount: Boolean(discount),
    originalPrice,
    currency,
    price: <FormattedCurrency value={price} currency={currency} />,
    priceValue: price ?? 0,
    discountOnlyFirstPurchase,
  })

  return (
    <div
      onClick={onClick}
      className="relative my-5 flex w-full min-w-full cursor-pointer items-center justify-center"
      style={{
        WebkitTapHighlightColor: 'transparent',
        opacity: selected ? 1 : 0.6,
      }}
    >
      <div
        className={cn(
          'pointer-events-none absolute inset-0 rounded-20 bg-white transition-transform duration-300 ease-in-out',
        )}
        style={{
          transform: selected ? 'scaleY(1)' : 'scaleY(0.9)',
          transformOrigin: 'center',
        }}
      />
      <div
        className={cn(
          `relative z-10 flex w-full flex-col justify-between rounded-20 border-0
        px-16 outline-none transition-transform duration-300 ease-in-out focus:border-0 focus:outline-none focus:ring-0
        active:border-0 active:outline-none`,
          selected ? 'py-18' : 'py-16',
          bestPlanInfo && (selected ? 'pt-28 lg:pt-48' : 'pt-26 lg:pt-40'),
        )}
      >
        {bestPlanInfo && React.cloneElement(bestPlanInfo, { selected })}
        <div
          className={`relative flex w-full items-start text-left md:flex-col lg:flex-row ${
            bestPlanInfo ? 'lg:mb-2' : 'lg:mb-7'
          }`}
        >
          <div className="w-full">
            <div className="order-reverse">
              <ChooseSubscriptionText
                duration={duration}
                discount={discount}
                discountOnlyFirstPurchase={discountOnlyFirstPurchase}
                originalPrice={originalPrice}
                currency={currency}
                price={price}
                isFallbackPrice={isFallbackPrice}
                durationText={durationText}
              />
            </div>
          </div>

          <div className="self-start sm:flex">
            {pricePerMonth != null && (
              <div className="flex items-center text-right text-14 font-demibold lg:text-17">
                <FormattedCurrency value={pricePerMonth} currency={currency} />
                <span className="text-TypeSub text-black">
                  /
                  <FormattedMessage id="SignUp.shortMon" defaultMessage="mon" />
                </span>
              </div>
            )}
          </div>
        </div>
        <div className="hidden text-left lg:block lg:w-full">
          <div className="type-small text-TypeSub text-black">
            {durationText}
          </div>
        </div>
        {Boolean(trialDuration) && (
          <div className="w-full text-left">
            <span
              className="text-14 lg:text-17"
              data-uid="plan_free_trial_info"
            >
              <FormattedMessage
                defaultMessage="{numberOfDays}-day free trial included"
                id="free_trial_50_check_2"
                values={{
                  numberOfDays: trialDuration,
                }}
              />
            </span>
          </div>
        )}
      </div>
    </div>
  )
}

export default ChooseSubscriptionProduct
